/**
 * TanStack Query hooks for the Pipeline Report (Reporting page).
 *
 * There is no create/delete — one row per `pipeline_report_entry`, auto-created by the
 * demand-link sync (see src/api/pipeline-report.ts header). Only PATCH exists.
 *
 * GET /api/reports/pipeline returns the full (securely scoped) result set in one call —
 * no cursor/search/limit params. Sort, filter, and pagination all happen client-side over
 * that one in-memory array (see PipelineReportTable.tsx) — an inline-edit PATCH never
 * triggers a re-sort/re-filter network round trip, only a cache invalidation.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  PipelineDealSyncResult,
  PipelineDealSyncRowResult,
  PipelineDealSyncRowStatus,
} from "@/db/sync/pipeline-deals";
import { jsonFetch } from "@/lib/json-fetch";
import { pipelineReportKeys } from "@/lib/query";
import type {
  PipelineReportEntryUpdateInput,
  PipelineReportImportChange,
  PipelineReportImportResult,
  PipelineReportImportRowResult,
  PipelineReportListPage,
  PipelineReportRow,
} from "@/schemas/pipeline-report";

export type {
  PipelineDealSyncResult,
  PipelineDealSyncRowResult,
  PipelineDealSyncRowStatus,
  PipelineReportEntryUpdateInput,
  PipelineReportImportChange,
  PipelineReportImportResult,
  PipelineReportImportRowResult,
  PipelineReportListPage,
  PipelineReportRow,
};

export function usePipelineReport() {
  return useQuery({
    queryKey: pipelineReportKeys.list(),
    queryFn: () => jsonFetch<PipelineReportListPage>("/api/reports/pipeline"),
    staleTime: 60_000,
  });
}

/**
 * No success toast — this is fired per-cell from inline table edits, sometimes several in
 * a row, and a toast per keystroke-adjacent save would be noise. The cell itself is the
 * success feedback (it optimistically shows the new value the moment it's committed; see
 * PipelineReportTable.tsx). Errors still toast, and the caller is responsible for rolling
 * its optimistic value back on error.
 */
export function useUpdatePipelineReportEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: PipelineReportEntryUpdateInput }) =>
      jsonFetch<PipelineReportRow>(`/api/reports/pipeline/${input.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input.patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pipelineReportKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * POST /api/reports/pipeline/import — parse + match + diff a CSV/XLSX against existing
 * pipeline_report_entry rows; only writes when `commit: true`. Every call (preview OR
 * commit) hits the network fresh — there is nothing to cache, so this is a mutation, not
 * a query.
 *
 * Deliberately does NOT invalidate `pipelineReportKeys` here: a `commit: false` preview
 * call changes nothing server-side, so refetching the table on every preview would be
 * pure noise (and would fight the caller's own optimistic table state). Only a
 * successful `commit: true` call should trigger a refetch — the caller (the import
 * dialog) does that itself, once, after it has shown the final result.
 */
export function useImportPipelineReport() {
  return useMutation({
    mutationFn: (payload: { file: File; commit: boolean }) => {
      const form = new FormData();
      form.append("file", payload.file);
      form.append("commit", String(payload.commit));
      return jsonFetch<PipelineReportImportResult>("/api/reports/pipeline/import", {
        method: "POST",
        body: form,
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * POST /api/reports/pipeline/sync — pull the full gateway Deal feed and reconcile it
 * into pipeline_report_entry (see src/db/sync/pipeline-deals.ts); only writes when
 * `commit: true`. Same "no invalidation here" posture as useImportPipelineReport: a
 * `commit: false` preview call changes nothing server-side, so only a successful
 * `commit: true` call should trigger a refetch — the caller (PipelineReportSyncDialog)
 * does that itself, once, after showing the final result.
 */
export function useSyncPipelineDeals() {
  return useMutation({
    mutationFn: (payload: { commit: boolean }) =>
      jsonFetch<PipelineDealSyncResult>("/api/reports/pipeline/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
    onError: (e: Error) => toast.error(e.message),
  });
}
